import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { t } from '@aquabox/shared';
import {
  useMatchAddress,
  useArchiveAllRegions,
  useMe,
  useRegionMutations,
  useRegions,
  useRematchPending,
  useTenant,
  useVehicles,
} from '../api/queries';
import { Link } from 'react-router-dom';
import { useToast } from '../components/Toast';
import { joinSentences, rematchSentence } from '../lib/rematch-message';
import { useApplyRegionSuggestions, type SuggestionChoice } from '../api/region-suggestions';
import type { ApiError } from '../api/client';
import { RegionSuggestions } from './RegionSuggestions';
import { NeighborhoodAliasesCard } from './NeighborhoodAliasesCard';
import type { Region } from '../api/types';

/**
 * Bölgeler — "Sade mahalle boya" ekranı (görev: bolgeler).
 *
 * Tek ekran, iki sütun:
 *   - Sol: bölge kartları (renk şeridi, ad, mahalle sayısı, araç seçici, Düzenle/Boyalı) + altta
 *     "+ Yeni Bölge" kartı (ad + renk + araç; mahalle YOK — mahalleler haritadan boyanır).
 *   - Sağ: harita = mahalle boyacı. Seçili bölge kartına tıklayınca `StreetMap` `mode='paint'` ve
 *     `paintRegionId=<bölge>` ile çalışır; poligon tıklaması `moveNeighborhood` ile doğrudan o bölgeye
 *     taşır (toggle — aynı bölgedeki mahalleye tıklamak çıkarır). Bölge seçili değilse mevcut
 *     "tıkla → bölge seç" akışı çalışır (NeighborhoodAssignPanel / RollbackFailedError korunur).
 *
 * Sokak katmanı (çizgiler, mod düğmeleri, Seçim paneli, sokak önerileri, sokak istisna kartı)
 * varsayılan GİZLİ — `StreetMap`'in paint yüzünde render edilmez. "Gelişmiş: sokak istisnaları"
 * açılır-kapanır bölümünde `mode='advanced'` ile eski davranış tam çalışır.
 *
 * Harita TEK örnektir (boyama aç/kapa haritayı yeniden kurmaz, zoom korunur). "Gelişmiş" bölümündeki
 * ikinci harita yalnız açıkken kurulur.
 *
 * "Bekleyen siparişleri yeniden dağıt" üstte duruyor; onaylar tarayıcı confirm() yerine modal.
 */

const EMPTY = {
  name: '',
  defaultVehicleId: '',
  autoAssign: true,
  color: '',
};

type Draft = typeof EMPTY;

// StreetMap (P6 sadeleştirme) lazy yüklenir; kullanıcı "Gelişmiş"i açarsa hazır olur.
const StreetMap = lazy(() => import('./StreetMap').then((m) => ({ default: m.StreetMap })));

export function Regions() {
  const toast = useToast();
  const list = useRegions();
  const vehicles = useVehicles();
  const { create, update, deactivate } = useRegionMutations();
  const rematch = useRematchPending();
  const tenant = useTenant();
  const me = useMe();
  const applySuggestions = useApplyRegionSuggestions();
  const archiveAll = useArchiveAllRegions();
  const isOwner = me.data?.role === 'OWNER';
  // "Verilerden bölge öner" paneli ve onay bekleyen taslaklar.
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [pendingApply, setPendingApply] = useState<SuggestionChoice[] | null>(null);
  // Uygula diyaloğu: önce mevcut bölgeleri arşivle (yalnız OWNER).
  const [archiveFirst, setArchiveFirst] = useState(false);
  // "Tümünü arşivle" onay diyaloğu.
  const [confirmArchive, setConfirmArchive] = useState(false);
  // Arşivden sonra öneri panelini sıfırdan kurar (bayat taslak/seçim kalmasın).
  const [suggestEpoch, setSuggestEpoch] = useState(0);
  const archiveLocked = archiveAll.isPending || applySuggestions.isPending;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<Region | null>(null);
  // Hangi bölge şu an "Boyalı" modunda? null = boyama kapalı.
  const [paintRegionId, setPaintRegionId] = useState<string | null>(null);
  // Gelişmiş sokak katmanı (varsayılan KAPALI — sade "mahalle boya" deneyimi).
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Rematch onay modalı.
  const [confirmRematch, setConfirmRematch] = useState(false);
  // Bölgeyi kapatma onayı.
  const [confirmClose, setConfirmClose] = useState<Region | null>(null);

  const items = list.data?.items ?? [];
  const activeVehicles = (vehicles.data?.items ?? []).filter((v) => v.isActive);
  const customerTotal = items.reduce((n, r) => n + (r._count?.customers ?? 0), 0);
  const paintRegion = useMemo(
    () => (paintRegionId ? (items.find((r) => r.id === paintRegionId) ?? null) : null),
    [paintRegionId, items],
  );

  // Esc: boya kipinden çık (kip yalnız kart düğmesine bağlı kalmasın).
  useEffect(() => {
    if (!paintRegionId) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setPaintRegionId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paintRegionId]);

  function startCreate(): void {
    setEditing(null);
    setDraft({ ...EMPTY });
  }

  function startEdit(r: Region): void {
    setPaintRegionId(null);
    setEditing(r);
    setDraft({
      name: r.name,
      defaultVehicleId: r.defaultVehicle?.id ?? '',
      autoAssign: r.autoAssign,
      color: r.color ?? '',
    });
  }

  function closeForm(): void {
    setPaintRegionId(null);
    setDraft(null);
    setEditing(null);
  }

  function submit(): void {
    if (!draft) return;
    if (draft.name.trim().length < 2) {
      toast(t('region.fieldRequired'), 'danger');
      return;
    }
    const body = {
      name: draft.name.trim(),
      // neighborhoods HARİÇ: yeni bölge mahallesiz başlar; mahalleler haritadan boyanır.
      defaultVehicleId: draft.defaultVehicleId || null,
      autoAssign: draft.autoAssign,
      color: draft.color.trim() || null,
    };
    if (editing) {
      update.mutate(
        { id: editing.id, ...body },
        {
          onSuccess: ({ rematch: r }) => {
            // Tek birleşik bildirim: arka arkaya iki toast birbirini ezerdi.
            toast(joinSentences(t('region.updated', { name: body.name }), rematchSentence(r)));
            closeForm();
          },
        },
      );
    } else {
      create.mutate(body, {
        onSuccess: ({ rematch: r }) => {
          toast(joinSentences(t('region.created', { name: body.name }), rematchSentence(r)));
          closeForm();
        },
      });
    }
  }

  function togglePaint(id: string): void {
    setPaintRegionId((cur) => (cur === id ? null : id));
  }

  function runRematch(): void {
    setConfirmRematch(false);
    rematch.mutate(undefined, {
      onSuccess: (data) => {
        toast(joinSentences(t('region.rematch.done'), rematchSentence(data)));
      },
      onError: () => toast(t('region.rematch.error'), 'danger'),
    });
  }

  function runApply(): void {
    const drafts = pendingApply;
    if (!drafts) return;
    const archive = archiveFirst && isOwner;
    setPendingApply(null);
    setArchiveFirst(false);
    if (archive) {
      // İki adımlı akış: arşivle, önerileri yeniden üret, kullanıcı taslakları yeniden onaylasın.
      // Bayat taslakla uygulanmaz: arşivden boşalan mahalleler eski taslakta yoktu.
      setPaintRegionId(null);
      archiveAll.mutate(undefined, {
        onSuccess: (r) => {
          setSuggestEpoch((n) => n + 1);
          toast(t('region.archiveAll.stepDone', { regions: r.archivedRegions }));
        },
        onError: () => toast(t('region.archiveAll.error'), 'danger'),
      });
      return;
    }
    const onError = (err: unknown): void => {
      // 409 (aynı ad) ve 400 (mahalle başka bölgede) sunucunun Türkçe mesajıyla gösterilir.
      const e = err as Partial<ApiError>;
      const known = e.status === 409 || e.status === 400;
      toast(known && e.message ? e.message : t('region.suggest.applyError'), 'danger');
    };
    applySuggestions.mutate(drafts, {
      onSuccess: ({ created }) => {
        setSuggestOpen(false);
        toast(t('region.suggest.applied', { count: created.length }));
      },
      onError,
    });
  }

  function runArchiveAll(): void {
    setConfirmArchive(false);
    closeForm();
    archiveAll.mutate(undefined, {
      onSuccess: (r) =>
        toast(
          t('region.archiveAll.done', {
            regions: r.archivedRegions,
            customers: r.detachedCustomers,
          }),
        ),
      onError: () => toast(t('region.archiveAll.error'), 'danger'),
    });
  }

  function runClose(): void {
    const r = confirmClose;
    if (!r) return;
    setConfirmClose(null);
    deactivate.mutate(r.id, {
      onSuccess: () => {
        if (paintRegionId === r.id) setPaintRegionId(null);
        // Kapatılan bölgenin açık düzenleme formu bayat kalmasın.
        if (editing?.id === r.id) closeForm();
        toast(t('region.deactivated', { name: r.name }));
      },
      onError: () => toast(t('region.deactivate.error', { name: r.name }), 'danger'),
    });
  }

  function changeVehicle(r: Region, vehicleId: string): void {
    update.mutate(
      { id: r.id, defaultVehicleId: vehicleId || null },
      {
        onSuccess: ({ rematch: res }) =>
          toast(
            joinSentences(t('region.card.vehicleSaved', { name: r.name }), rematchSentence(res)),
          ),
        onError: () => toast(t('region.card.saveError', { name: r.name }), 'danger'),
      },
    );
  }

  return (
    <div style={{ padding: '28px 32px 36px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{t('region.title')}</h1>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.6,
              color: 'var(--text-secondary)',
              maxWidth: 720,
              margin: 0,
            }}
          >
            {t('region.intro')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn"
            type="button"
            onClick={() => setConfirmRematch(true)}
            disabled={rematch.isPending}
            data-testid="region-rematch-btn"
          >
            {t('region.rematch.button')}
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => setSuggestOpen((v) => !v)}
            data-testid="region-suggest-btn"
          >
            {t('region.suggest.button')}
          </button>
          {isOwner ? (
            <button
              className="btn"
              type="button"
              onClick={() => setConfirmArchive(true)}
              disabled={items.length === 0 || archiveLocked}
              data-testid="region-archive-all-btn"
            >
              {t('region.archiveAll.button')}
            </button>
          ) : null}
          <button
            className="btn btn-primary"
            onClick={startCreate}
            disabled={editing !== null}
            data-testid="region-add-btn"
          >
            {t('region.add')}
          </button>
        </div>
      </header>

      {suggestOpen ? (
        <RegionSuggestions
          key={suggestEpoch}
          isOwner={isOwner}
          busy={archiveLocked}
          vehicles={activeVehicles}
          onClose={() => setSuggestOpen(false)}
          onApply={(choices) => setPendingApply(choices)}
        />
      ) : null}

      {/* Kurallar yalnız AUTO'da işler; MANUAL'da bölge/sokak kuralı kurulur ama sipariş atanmaz. */}
      {tenant.data?.settings?.orderAssignmentMode === 'MANUAL' ? (
        <div
          role="status"
          className="card"
          data-testid="region-manual-banner"
          style={{ padding: 12, fontSize: 13.5, display: 'flex', gap: 8, flexWrap: 'wrap' }}
        >
          <span>{t('region.manualBanner')}</span>
          <Link to="/ayarlar">{t('region.manualBannerLink')}</Link>
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(320px, 380px) 1fr',
          gap: 18,
          alignItems: 'flex-start',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {paintRegion ? (
            <div
              className="card"
              data-testid="region-paint-banner"
              style={{
                padding: 14,
                background: 'var(--surface-2, var(--surface))',
                border: '1px solid var(--primary)',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <strong style={{ fontSize: 14 }}>{t('region.paintMode')}</strong>
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {paintRegion.name}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {t('region.paintEscHint')}
              </span>
              <button
                type="button"
                className="btn btn-primary"
                style={{ height: 34, fontSize: 13, alignSelf: 'flex-start' }}
                onClick={() => setPaintRegionId(null)}
                data-testid="region-paint-done"
              >
                {t('region.paintDone')}
              </button>
            </div>
          ) : null}

          {items.length === 0 && !draft ? (
            <p
              style={{
                fontSize: 13.5,
                color: 'var(--text-secondary)',
                margin: 0,
                padding: 16,
              }}
            >
              {t('region.empty')}
            </p>
          ) : null}

          {items.map((r) =>
            editing && editing.id === r.id && draft ? (
              <RegionForm
                key={r.id}
                draft={draft}
                onChange={setDraft}
                vehicles={activeVehicles}
                onCancel={closeForm}
                onSubmit={submit}
                pending={update.isPending}
                error={update.error}
                submitLabel={t('region.saveEdit')}
                onClose={() => setConfirmClose(r)}
              />
            ) : (
              <RegionCard
                key={r.id}
                region={r}
                vehicles={activeVehicles}
                isPaining={paintRegionId === r.id}
                onPaintToggle={() => togglePaint(r.id)}
                onEdit={() => startEdit(r)}
                onVehicle={(vehicleId) => changeVehicle(r, vehicleId)}
                vehiclePending={update.isPending}
              />
            ),
          )}

          {draft && !editing ? (
            <RegionForm
              draft={draft}
              onChange={setDraft}
              vehicles={activeVehicles}
              onCancel={closeForm}
              onSubmit={submit}
              pending={create.isPending}
              error={create.error}
              submitLabel={t('region.saveNew')}
            />
          ) : null}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {paintRegion ? (
            <p
              style={{
                fontSize: 12.5,
                color: 'var(--text-secondary)',
                margin: 0,
                padding: '0 4px',
              }}
            >
              {t('region.paintHint')}
            </p>
          ) : null}
          <Suspense
            fallback={
              <div className="card" style={{ padding: 20 }}>
                <p style={{ margin: 0, fontSize: 13.5 }}>{t('streetMap.loading')}</p>
              </div>
            }
          >
            {/* TEK harita: boyama aç/kapa yalnız prop değiştirir, harita yeniden kurulmaz (zoom korunur). */}
            <div data-testid="region-map" data-paint-region={paintRegionId ?? ''}>
              <StreetMap mode="paint" paintRegionId={paintRegionId} />
            </div>
          </Suspense>
        </div>
      </div>

      <details
        open={advancedOpen}
        onToggle={(e) => setAdvancedOpen((e.target as HTMLDetailsElement).open)}
        className="card"
        data-testid="region-advanced"
        style={{ padding: 0, overflow: 'hidden' }}
      >
        <summary
          style={{
            cursor: 'pointer',
            listStyle: 'none',
            padding: '14px 18px',
            fontWeight: 600,
            fontSize: 13.5,
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span>{t('region.advanced.toggle')}</span>
          <span style={{ marginLeft: 'auto', fontSize: 12 }}>
            {advancedOpen ? t('region.advanced.close') : t('region.advanced.open')}
          </span>
        </summary>
        <div style={{ padding: 4, borderTop: '1px solid var(--border)' }}>
          {advancedOpen ? (
            <Suspense fallback={<p style={{ padding: 16 }}>{t('streetMap.loading')}</p>}>
              <StreetMap mode="advanced" />
            </Suspense>
          ) : null}
        </div>
      </details>

      <AddressMatcher />

      <NeighborhoodAliasesCard
        isOwner={isOwner}
        aliases={tenant.data?.settings?.neighborhoodAliases ?? {}}
        regions={items}
      />

      {confirmRematch ? (
        <ConfirmModal
          idPrefix="region-rematch"
          title={t('region.rematch.confirmTitle')}
          body={t('region.rematch.confirmBody')}
          okLabel={t('region.rematch.confirmOk')}
          cancelLabel={t('region.rematch.confirmCancel')}
          onCancel={() => setConfirmRematch(false)}
          onOk={runRematch}
        />
      ) : null}

      {pendingApply ? (
        <ConfirmModal
          idPrefix="region-suggest"
          title={
            archiveFirst ? t('region.archiveAll.confirmTitle') : t('region.suggest.confirmTitle')
          }
          body={
            archiveFirst
              ? t('region.archiveAll.confirmBody', {
                  regions: items.length,
                  customers: customerTotal,
                })
              : t('region.suggest.confirmBody', { count: pendingApply.length })
          }
          okLabel={archiveFirst ? t('region.suggest.archiveOk') : t('region.suggest.confirmOk')}
          cancelLabel={t('region.rematch.confirmCancel')}
          onCancel={() => {
            setPendingApply(null);
            setArchiveFirst(false);
          }}
          onOk={runApply}
        >
          {isOwner && items.length > 0 ? (
            <label style={{ display: 'flex', gap: 8, fontSize: 13.5, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={archiveFirst}
                onChange={(e) => setArchiveFirst(e.target.checked)}
                data-testid="region-suggest-archive-first"
              />
              <span>
                {t('region.suggest.archiveFirst', {
                  regions: items.length,
                  customers: customerTotal,
                })}
              </span>
            </label>
          ) : null}
        </ConfirmModal>
      ) : null}

      {confirmArchive ? (
        <ConfirmModal
          idPrefix="region-archive-all"
          title={t('region.archiveAll.confirmTitle')}
          body={t('region.archiveAll.confirmBody', {
            regions: items.length,
            customers: customerTotal,
          })}
          okLabel={t('region.archiveAll.confirmOk')}
          cancelLabel={t('region.cancel')}
          onCancel={() => setConfirmArchive(false)}
          onOk={runArchiveAll}
        />
      ) : null}

      {confirmClose ? (
        <ConfirmModal
          idPrefix="region-close"
          title={t('region.deactivate.confirmTitle')}
          body={t('region.deactivate.confirmBody', { name: confirmClose.name })}
          okLabel={t('region.deactivate.confirmOk')}
          cancelLabel={t('region.cancel')}
          onCancel={() => setConfirmClose(null)}
          onOk={runClose}
        />
      ) : null}
    </div>
  );
}

/** Uygulamanın onay penceresi deseni (tarayıcı confirm() YOK). */
function ConfirmModal({
  idPrefix,
  title,
  body,
  okLabel,
  cancelLabel,
  onCancel,
  onOk,
  children,
}: {
  /** Test kimlikleri: `<idPrefix>-confirm` (kap), `<idPrefix>-cancel`, `<idPrefix>-ok`. */
  idPrefix: string;
  title: string;
  body: string;
  okLabel: string;
  cancelLabel: string;
  onCancel: () => void;
  onOk: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div
      data-testid={`${idPrefix}-confirm`}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: 420,
          width: '90%',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <strong style={{ fontSize: 15 }}>{title}</strong>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>{body}</p>
        {children}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn"
            style={{ flex: 1 }}
            onClick={onCancel}
            data-testid={`${idPrefix}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={onOk}
            data-testid={`${idPrefix}-ok`}
          >
            {okLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function RegionCard({
  region,
  vehicles,
  isPaining,
  onPaintToggle,
  onEdit,
  onVehicle,
  vehiclePending,
}: {
  region: Region;
  vehicles: { id: string; plate: string; label: string | null }[];
  isPaining: boolean;
  onPaintToggle: () => void;
  onEdit: () => void;
  onVehicle: (vehicleId: string) => void;
  vehiclePending: boolean;
}) {
  const customers = region._count?.customers ?? 0;
  return (
    <div
      className="card"
      data-testid="region-card"
      data-region-id={region.id}
      style={{
        padding: 0,
        display: 'grid',
        gridTemplateColumns: '8px 1fr',
        overflow: 'hidden',
      }}
    >
      <span
        aria-hidden="true"
        style={{ background: region.color ?? 'var(--primary)' }}
        title={region.color ?? undefined}
      />
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <strong style={{ fontSize: 15 }}>{region.name}</strong>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {region.neighborhoods.length > 0
              ? t('region.card.neighborhoodCount', { count: region.neighborhoods.length })
              : t('region.card.noNeighborhoods')}
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>
            {customers > 0
              ? t('region.customersCount', { count: customers })
              : t('region.noCustomers')}
          </span>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ color: 'var(--text-secondary)' }}>{t('region.defaultVehicle')}</span>
          <select
            value={region.defaultVehicle?.id ?? ''}
            onChange={(e) => onVehicle(e.target.value)}
            disabled={vehiclePending}
            style={{ height: 34, flex: 1, minWidth: 0 }}
            data-testid="region-card-vehicle"
          >
            <option value="">{t('region.card.addVehicle')}</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.plate}
                {v.label ? ` · ${v.label}` : ''}
              </option>
            ))}
          </select>
        </label>
        {!region.defaultVehicle ? (
          <span
            role="note"
            style={{ fontSize: 12.5, color: 'var(--warning)' }}
            data-testid="region-card-no-vehicle"
          >
            {t('region.card.noVehicleWarning')}
          </span>
        ) : null}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button
            className={isPaining ? 'btn btn-primary' : 'btn'}
            style={{ height: 34, fontSize: 13 }}
            type="button"
            aria-pressed={isPaining}
            onClick={onPaintToggle}
            data-testid="region-paint-toggle"
          >
            {isPaining ? t('region.card.paintMode') : t('region.card.paintModeOff')}
          </button>
          <button
            className="btn"
            style={{ height: 34, fontSize: 13 }}
            type="button"
            onClick={onEdit}
            data-testid="region-edit"
          >
            {t('region.card.edit')}
          </button>
        </div>
      </div>
    </div>
  );
}

function RegionForm({
  draft,
  onChange,
  vehicles,
  onCancel,
  onSubmit,
  pending,
  error,
  submitLabel,
  onClose,
}: {
  draft: Draft;
  onChange: (d: Draft) => void;
  vehicles: { id: string; plate: string; label: string | null }[];
  onCancel: () => void;
  onSubmit: () => void;
  pending: boolean;
  error: unknown;
  submitLabel: string;
  /** Yalnız düzenlemede: bölgeyi kapat (yıkıcı işlem kartta değil, burada). */
  onClose?: () => void;
}) {
  // 400 BadRequest'leri alan bazlı göstermek için ZodPipe issues'larına bakıyoruz.
  // `regions.service.ts#create` renk atamayı kendisi yapıyor; boş bırakılırsa paletten seçer.
  const apiErr = error as ApiError | null;
  const issues = apiErr?.issues ?? [];
  const otherIssue = issues.find((i) => i.path !== 'neighborhoods');
  const valid = draft.name.trim().length >= 2 && !pending;

  return (
    <div
      className="card"
      style={{
        padding: 18,
        borderColor: 'var(--primary)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div
        className="form-grid"
        style={
          {
            '--form-cols': '1fr 200px 110px',
            '--form-gap': '10px',
            alignItems: 'end',
          } as React.CSSProperties
        }
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="section-label">{t('region.name')}</span>
          <input
            value={draft.name}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            placeholder={t('region.namePlaceholder')}
            style={{ height: 44 }}
            data-testid="region-name"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="section-label">{t('region.defaultVehicle')}</span>
          <select
            value={draft.defaultVehicleId}
            onChange={(e) => onChange({ ...draft, defaultVehicleId: e.target.value })}
            style={{ height: 44 }}
            data-testid="region-vehicle"
          >
            <option value="">{t('region.noVehicle')}</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.plate}
                {v.label ? ` · ${v.label}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="section-label">{t('region.color')}</span>
          <input
            type="color"
            value={draft.color || '#1E5BB8'}
            onChange={(e) => onChange({ ...draft, color: e.target.value })}
            style={{ height: 44, padding: 4 }}
            title={t('region.colorHint')}
            data-testid="region-color"
          />
        </label>
      </div>

      {!draft.color ? (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
          {t('region.addForm.colorAuto')}
        </p>
      ) : null}

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 13.5,
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={draft.autoAssign}
          onChange={(e) => onChange({ ...draft, autoAssign: e.target.checked })}
          style={{ width: 18, height: 18 }}
        />
        <span>
          <strong>{t('region.autoAssign')}</strong>
          <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>
            {t('region.autoAssignHint')}
          </span>
        </span>
      </label>

      {otherIssue ? (
        <p role="alert" style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
          {otherIssue.message}
        </p>
      ) : error ? (
        <ErrorLine error={error} />
      ) : null}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" type="button" onClick={onCancel} disabled={pending}>
          {t('region.cancel')}
        </button>
        <button
          className="btn btn-primary"
          type="button"
          onClick={onSubmit}
          disabled={!valid}
          data-testid="region-save"
        >
          {submitLabel}
        </button>
      </div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          disabled={pending}
          data-testid="region-deactivate"
          style={{
            alignSelf: 'flex-start',
            background: 'transparent',
            border: 0,
            padding: 0,
            color: 'var(--danger)',
            fontSize: 12.5,
            cursor: 'pointer',
          }}
        >
          {t('region.deactivate')}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Adres eşleme denemesi (§1.4): hızlı doğrulama aracı. Yanıt panelin içinde kalır, listeyi
 * etkilemez — mahalle listesi değişene kadar aynı adres aynı sonucu vermeli.
 */
function AddressMatcher() {
  const match = useMatchAddress();
  const [address, setAddress] = useState('');

  function tryMatch(): void {
    const a = address.trim();
    if (a.length < 3) {
      match.reset();
      return;
    }
    match.mutate(a);
  }

  return (
    <div
      className="card"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <strong style={{ fontSize: 14 }}>{t('region.matcher')}</strong>
        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {t('region.matcherHint')}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') tryMatch();
          }}
          placeholder={t('region.matcherPlaceholder')}
          style={{ height: 44, flex: 1 }}
          data-testid="region-matcher-input"
        />
        <button
          className="btn btn-primary"
          onClick={tryMatch}
          disabled={address.trim().length < 3 || match.isPending}
        >
          {t('region.matcherTry')}
        </button>
      </div>
      {match.data ? <MatchResult data={match.data} /> : null}
      {match.isError ? <ErrorLine error={match.error} /> : null}
    </div>
  );
}

function MatchResult({
  data,
}: {
  data: {
    region: { id: string; name: string } | null;
    vehicle: { id: string; plate: string } | null;
    autoAssign: boolean;
  };
}) {
  if (!data.region) {
    return (
      <p style={{ fontSize: 13.5, color: 'var(--warning)', margin: 0 }}>
        {t('region.match.unmatched')}
      </p>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 13.5 }}>
        <strong>{t('region.match.matchedRegion', { name: data.region.name })}</strong>
        {data.vehicle ? (
          <>
            {' · '}
            {t('region.match.matchedVehicle', { plate: data.vehicle.plate })}
          </>
        ) : null}
      </span>
      <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
        {data.autoAssign ? t('region.match.matchedAuto') : t('region.match.matchedManual')}
      </span>
    </div>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const message = (error as { message?: string } | null)?.message;
  return (
    <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
      {message && message.length > 0 ? message : t('error.internal')}
    </p>
  );
}
